import { getTimelineEntryDate } from '../../../services/timeline/dateUtils';
import { getCalendarDateKey } from '../../../utils/calendarDateKey';
import { formatTimelineTime } from '../../../hooks/timeline/timelineDateUtils';
import { buildTimelinePresentationItemsForDate } from '../../Timeline/utils/timelinePresentationCompositor';
import { buildHomeProfileUpdateSummary } from './homeProfileUpdateSummary';

export const getHomeEntryDate = (entry) => {
  if (!entry) {
    return null;
  }

  const candidate = getTimelineEntryDate(entry)
    || entry.updatedAt?.toDate?.()
    || entry.updatedAt
    || (typeof entry.timestamp?.toDate === 'function' ? entry.timestamp.toDate() : null)
    || new Date(entry.timestamp);

  return candidate instanceof Date && !Number.isNaN(candidate.getTime()) ? candidate : null;
};

export const getHomeEntryCategory = (entry = {}) => (
  entry.category
  || entry.type
  || entry.timelineType
  || entry.actionType
  || entry.logCategory
  || 'log'
);

export const getHomeEntryText = (entry = {}) => String(
  entry.sourceReceiptTitle
  || entry.content
  || entry.text
  || entry.notes
  || entry.description
  || entry.summary
  || entry.title
  || entry.label
  || 'Logged update'
).trim();

export const getHomeEntrySecondaryLabel = (entry = {}) => String(
  entry.homeSecondaryLabel || entry.sourceReceiptSummary || ''
).trim();

export const getHomeEntryTimeLabel = (entry) => formatTimelineTime(entry) || '';

const isLiveEntry = (entry) => entry?.status !== 'deleted';

const getSourceNoteId = (entry = {}) => String(
  entry.sourceNoteId
  || entry.metadata?.sourceNoteId
  || entry.source?.sourceNoteId
  || ''
).trim();

const isSourceGeneratedFallback = (entry = {}) => {
  if (!getSourceNoteId(entry)) return false;

  const planner = entry.metadata?.timelinePlanner || entry.timelinePlanner || {};
  const fallbackMarkers = [
    entry.plannerMode,
    entry.parserMode,
    entry.timestampSource,
    planner.plannerMode,
    planner.eventKind,
  ].map((value) => String(value || '').toLowerCase());

  return Boolean(
    entry.sourceSavedAsOriginalNote
    || entry.metadata?.savedAsOriginalNote
    || fallbackMarkers.some((value) => value.includes('original_note_fallback'))
  );
};

const isGenericProfileOrganizationEntry = (entry = {}, profileSourceIds = new Set()) => {
  const sourceNoteId = getSourceNoteId(entry);
  if (!sourceNoteId || !profileSourceIds.has(sourceNoteId)) return false;
  if (isSourceGeneratedFallback(entry)) return true;

  const category = String(getHomeEntryCategory(entry)).trim().toLowerCase();
  if (!['log', 'note', 'daily_log', 'dailylog'].includes(category)) return false;

  const planner = entry.metadata?.timelinePlanner || entry.timelinePlanner || {};
  const eventKind = String(planner.eventKind || entry.eventKind || '').trim().toLowerCase();
  const genericEventKinds = new Set(['', 'daily_log', 'general_update', 'profile_update', 'original_note_fallback']);
  const title = String(entry.title || entry.titlePrefix || '').trim().toLowerCase();
  const genericTitles = new Set(['', 'daily log', 'logged update', 'original note', 'care note']);

  return genericEventKinds.has(eventKind) && genericTitles.has(title);
};

const getReceiptStatus = (receipt = {}) => {
  const summary = String(receipt.sourceReceiptSummary || '').toLowerCase();
  if (summary.includes('needs attention')) return 'failed';
  if (summary.includes('being organized')) return 'processing';
  return 'completed';
};

const pluralizeNotes = (count) => `${count} ${count === 1 ? 'note' : 'notes'}`;

const buildSourceActivitySummary = ({ receipts = [], fallbackEntries = [] } = {}) => {
  if (!receipts.length && !fallbackEntries.length) return null;

  const sources = new Map();
  receipts.forEach((receipt) => {
    const sourceNoteId = getSourceNoteId(receipt);
    if (!sourceNoteId) return;
    sources.set(sourceNoteId, {
      status: getReceiptStatus(receipt),
      entryCount: Number(receipt.sourceStorySceneCount || 0),
    });
  });
  fallbackEntries.forEach((entry) => {
    const sourceNoteId = getSourceNoteId(entry);
    if (!sourceNoteId || sources.has(sourceNoteId)) return;
    sources.set(sourceNoteId, { status: 'completed', entryCount: 1 });
  });

  const sourceActivity = [...sources.values()];
  const failedCount = sourceActivity.filter(({ status }) => status === 'failed').length;
  const processingCount = sourceActivity.filter(({ status }) => status === 'processing').length;
  const completedSources = sourceActivity.filter(({ status }) => status === 'completed');
  const completedCount = completedSources.length;
  const organizedEntryCount = completedSources.reduce((total, source) => total + source.entryCount, 0);

  let title = `${pluralizeNotes(sourceActivity.length)} added today`;
  if (failedCount) title = `${pluralizeNotes(failedCount)} ${failedCount === 1 ? 'needs' : 'need'} attention`;
  else if (processingCount) title = `${pluralizeNotes(processingCount)} ${processingCount === 1 ? 'is' : 'are'} still being organized`;

  const details = [];
  if (failedCount && processingCount) details.push(`${processingCount} still organizing`);
  if ((failedCount || processingCount) && completedCount) details.push(`${completedCount} added`);
  if (organizedEntryCount) {
    details.push(`${organizedEntryCount} care ${organizedEntryCount === 1 ? 'entry' : 'entries'} organized`);
  }

  return {
    id: 'home-source-activity-summary',
    kind: 'source_activity_summary',
    category: 'note',
    title,
    homeSecondaryLabel: details.join(' · '),
    sourceCount: sourceActivity.length,
    failedCount,
    processingCount,
    completedCount,
    organizedEntryCount,
  };
};

const sortNewestFirst = (entries) => (
  [...entries].sort((a, b) => getHomeEntryDate(b) - getHomeEntryDate(a))
);

/**
 * Today's entries, newest first, capped at `limit`.
 */
export const selectTodayEntries = (entries = [], referenceDate = new Date(), limit = 5) => {
  const todayKey = getCalendarDateKey(referenceDate);
  return sortNewestFirst(
    entries.filter((entry) => {
      if (!isLiveEntry(entry)) return false;
      const entryDate = getHomeEntryDate(entry);
      return entryDate && getCalendarDateKey(entryDate) === todayKey;
    })
  ).slice(0, limit);
};

/**
 * Home's Today preview uses the same presentation composition as Timeline so
 * source captures appear as receipts rather than repeated full source text.
 * Care events stay ahead of receipt-only activity.
 */
export const selectTodayPresentationEntries = (
  entries = [],
  referenceDate = new Date(),
  limit = 5,
  { profileUpdateReceipts = [], personName = 'Profile' } = {}
) => {
  const liveEntries = entries.filter(isLiveEntry);
  const todayEntries = selectTodayEntries(liveEntries, referenceDate, Number.MAX_SAFE_INTEGER);
  const presentationItems = buildTimelinePresentationItemsForDate({
    entries: todayEntries,
    calendarEntries: liveEntries,
    selectedDate: referenceDate,
  });

  const profileUpdateSummary = buildHomeProfileUpdateSummary(
    profileUpdateReceipts,
    referenceDate,
    personName
  );
  const profileSourceIds = new Set(profileUpdateSummary?.sourceIds || []);
  const receipts = presentationItems.filter((entry) => (
    entry.kind === 'source_receipt' && !profileSourceIds.has(getSourceNoteId(entry))
  ));
  const fallbackEntries = presentationItems.filter((entry) => (
    isSourceGeneratedFallback(entry) && !profileSourceIds.has(getSourceNoteId(entry))
  ));
  const meaningfulEntries = presentationItems
    .filter((entry) => (
      entry.kind !== 'source_receipt'
      && !isSourceGeneratedFallback(entry)
      && !isGenericProfileOrganizationEntry(entry, profileSourceIds)
    ))
    .sort((left, right) => getHomeEntryDate(right) - getHomeEntryDate(left))
    .slice(0, limit);
  const sourceActivitySummary = buildSourceActivitySummary({ receipts, fallbackEntries });

  return [
    ...meaningfulEntries,
    ...(profileUpdateSummary ? [profileUpdateSummary] : []),
    ...(sourceActivitySummary ? [sourceActivitySummary] : []),
  ];
};

/**
 * The most recent entries strictly before today, newest first, capped at `limit`.
 * Deliberately excludes today so it never duplicates selectTodayEntries.
 */
export const selectRecentCareEntries = (entries = [], referenceDate = new Date(), limit = 3) => {
  const todayKey = getCalendarDateKey(referenceDate);
  return sortNewestFirst(
    entries.filter((entry) => {
      if (!isLiveEntry(entry)) return false;
      const entryDate = getHomeEntryDate(entry);
      return entryDate && getCalendarDateKey(entryDate) !== todayKey;
    })
  ).slice(0, limit);
};

export const selectLoggingRhythm = (entries = [], referenceDate = new Date()) => {
  const end = new Date(referenceDate);
  end.setHours(23, 59, 59, 999);
  const start = new Date(referenceDate);
  start.setDate(start.getDate() - 6);
  start.setHours(0, 0, 0, 0);

  const loggedDays = new Set(entries.flatMap((entry) => {
    if (!isLiveEntry(entry)) return [];
    const entryDate = getHomeEntryDate(entry);
    if (!entryDate || entryDate < start || entryDate > end) return [];
    return [getCalendarDateKey(entryDate)];
  }));

  return {
    daysLogged: loggedDays.size,
    message: loggedDays.size
      ? `You captured care on ${loggedDays.size} of the last 7 days.`
      : 'Start with one detail today.',
  };
};
